import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import PropTypes from 'prop-types'
import React from 'react'

import { colors } from 'app/theme/colors'

export const UNASSIGNED = '__unassigned__'
export const NO_CENTRE = '__no_centre__'

const useStyles = makeStyles((theme) => ({
  header: { marginBottom: theme.spacing(2) },
  subtitle: { color: colors.textSecondary },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  quickFilters: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  muted: { color: colors.textSecondary },
  centreChips: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  warning: { marginTop: theme.spacing(1.5), color: colors.textSecondary },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: colors.textTertiary
  }
}))

/**
 * Who holds which role, and who may use Alfred at all.
 *
 * Only admins and teachers appear — students, parents and guardians cannot hold
 * a role. Somebody who is both a teacher AND an admin shows up once, with both.
 *
 * Status is backed by User.is_active, which really does gate Alfred (the backend
 * 403s their Alfred requests — see AlfredAccessEnabled). Switching someone off
 * does NOT touch the teacher mobile app: the gate only bites requests carrying
 * Alfred's `source` header.
 *
 * Centres come from AdminUser.centres, which is a many-to-many in the database
 * but is edited here as exactly one centre per person. So the column can SHOW
 * more than it can set, and setting one replaces the rest — hence the confirm
 * dialog. Teachers have no centres field at all (Teacher.primary_centre is
 * derived from their batches), so a teacher-only row shows a dash.
 */
const People = ({
  people,
  roles,
  centres,
  roleFilter,
  currentUserUuid,
  savingUuid,
  onRoleFilterChange,
  onAssignRole,
  onAssignCentre,
  onToggleAccess
}) => {
  const classes = useStyles()
  const [query, setQuery] = React.useState('')
  // 'all' | 'active' | 'inactive' — client-side, like the search box.
  const [statusFilter, setStatusFilter] = React.useState('all')
  // { person, centre } awaiting confirmation; centre is null when clearing.
  const [pendingCentre, setPendingCentre] = React.useState(null)

  const countFor = (roleUuid) =>
    people.filter((person) =>
      roleUuid === UNASSIGNED ? !person.role : person.role?.uuid === roleUuid
    ).length

  const visiblePeople = people.filter((person) => {
    const matchesRole =
      roleFilter === 'all' ||
      (roleFilter === UNASSIGNED
        ? !person.role
        : person.role?.uuid === roleFilter)

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' ? person.enabled : !person.enabled)

    const needle = query.trim().toLowerCase()
    const matchesQuery =
      !needle ||
      person.name.toLowerCase().includes(needle) ||
      person.phone.toLowerCase().includes(needle) ||
      person.email.toLowerCase().includes(needle)

    return matchesRole && matchesStatus && matchesQuery
  })

  // Only an admin has an AdminUser row to hang centres off; PATCHing the centre
  // of a teacher-only person would 404.
  const canHoldCentre = (person) => person.kinds.includes('Admin')

  const requestCentreChange = (person, value) => {
    const centre =
      value === NO_CENTRE
        ? null
        : centres.find((each) => each.uuid === value) || null
    setPendingCentre({ person, centre })
  }

  const confirmCentreChange = () => {
    const { person, centre } = pendingCentre
    setPendingCentre(null)
    onAssignCentre(person.uuid, centre ? centre.uuid : null)
  }

  // What the pending change would take away: everything they hold that is not
  // the centre being set.
  const centresBeingRemoved = pendingCentre
    ? (pendingCentre.person.centres || []).filter(
        (each) => each.uuid !== pendingCentre.centre?.uuid
      )
    : []

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          People by role
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Admins and teachers only — a role cannot be given to a student, parent
          or guardian. Switching someone to Inactive blocks them from Alfred; it
          does not affect the teacher mobile app.
        </Typography>
      </Box>

      <Box className={classes.quickFilters}>
        <Chip
          size="small"
          label={`All (${people.length})`}
          color={roleFilter === 'all' ? 'primary' : 'default'}
          variant={roleFilter === 'all' ? 'default' : 'outlined'}
          onClick={() => onRoleFilterChange('all')}
        />
        {roles.map((role) => (
          <Chip
            key={role.uuid}
            size="small"
            label={`${role.name} (${countFor(role.uuid)})`}
            color={roleFilter === role.uuid ? 'primary' : 'default'}
            variant={roleFilter === role.uuid ? 'default' : 'outlined'}
            onClick={() => onRoleFilterChange(role.uuid)}
          />
        ))}
        <Chip
          size="small"
          label={`No role (${countFor(UNASSIGNED)})`}
          color={roleFilter === UNASSIGNED ? 'primary' : 'default'}
          variant={roleFilter === UNASSIGNED ? 'default' : 'outlined'}
          onClick={() => onRoleFilterChange(UNASSIGNED)}
        />
      </Box>

      <Box className={classes.filters}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
          <InputLabel id="role-filter-label">Role</InputLabel>
          <Select
            labelId="role-filter-label"
            label="Role"
            value={roleFilter}
            onChange={(event) => onRoleFilterChange(event.target.value)}
          >
            <MenuItem value="all">All roles</MenuItem>
            {roles.map((role) => (
              <MenuItem key={role.uuid} value={role.uuid}>
                {role.name}
              </MenuItem>
            ))}
            <MenuItem value={UNASSIGNED}>No role</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        <TextField
          placeholder="Search name, phone or email"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        <Typography variant="body2" className={classes.subtitle}>
          {visiblePeople.length} of {people.length} people
        </Typography>
      </Box>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Centre</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visiblePeople.map((person) => (
              <TableRow key={person.uuid} hover>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {person.name}
                  </Typography>
                  <Typography variant="caption" className={classes.muted}>
                    {person.phone}
                    {person.email ? ` · ${person.email}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  {person.kinds.map((kind) => (
                    <Chip
                      key={kind}
                      size="small"
                      variant="outlined"
                      label={kind}
                      style={{ marginRight: 4 }}
                    />
                  ))}
                </TableCell>
                <TableCell>
                  <Select
                    value={person.role?.uuid || UNASSIGNED}
                    disabled={savingUuid === person.uuid}
                    onChange={(event) =>
                      onAssignRole(
                        person.uuid,
                        event.target.value === UNASSIGNED
                          ? null
                          : event.target.value
                      )
                    }
                    disableUnderline
                    style={{ minWidth: 180 }}
                    renderValue={(value) =>
                      value === UNASSIGNED ? (
                        <Typography variant="body2" className={classes.muted}>
                          No role — sees everything
                        </Typography>
                      ) : (
                        <Chip
                          size="small"
                          color="primary"
                          label={
                            roles.find((role) => role.uuid === value)?.name ||
                            person.role?.name
                          }
                        />
                      )
                    }
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.uuid} value={role.uuid}>
                        {role.name}
                      </MenuItem>
                    ))}
                    <MenuItem value={UNASSIGNED}>
                      No role — sees everything
                    </MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  {canHoldCentre(person) ? (
                    <Select
                      // A person can hold several centres in the database even
                      // though Alfred only ever sets one, so the value tracks
                      // the first while renderValue shows all of them.
                      value={person.centres?.[0]?.uuid || NO_CENTRE}
                      disabled={savingUuid === person.uuid}
                      onChange={(event) =>
                        requestCentreChange(person, event.target.value)
                      }
                      disableUnderline
                      style={{ minWidth: 160 }}
                      renderValue={() =>
                        person.centres?.length ? (
                          <Box className={classes.centreChips}>
                            {person.centres.map((centre) => (
                              <Chip
                                key={centre.uuid}
                                size="small"
                                variant="outlined"
                                label={centre.name}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" className={classes.muted}>
                            No centre
                          </Typography>
                        )
                      }
                    >
                      {centres.map((centre) => (
                        <MenuItem key={centre.uuid} value={centre.uuid}>
                          {centre.name}
                        </MenuItem>
                      ))}
                      <MenuItem value={NO_CENTRE}>No centre</MenuItem>
                    </Select>
                  ) : (
                    <Tooltip title="Only admins are mapped to a centre — a teacher's centre comes from their batches">
                      <Typography variant="body2" className={classes.muted}>
                        —
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      person.uuid === currentUserUuid
                        ? 'You cannot disable your own access'
                        : person.enabled
                        ? 'Active — can use Alfred'
                        : 'Inactive — cannot use Alfred'
                    }
                  >
                    <span>
                      <Switch
                        size="small"
                        color="primary"
                        checked={person.enabled}
                        // Disabling yourself would lock you out of the screen
                        // that could undo it. The backend refuses it too.
                        disabled={
                          person.uuid === currentUserUuid ||
                          savingUuid === person.uuid
                        }
                        onChange={(event) =>
                          onToggleAccess(person.uuid, event.target.checked)
                        }
                      />
                    </span>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    className={person.enabled ? undefined : classes.muted}
                  >
                    {person.enabled ? 'Active' : 'Inactive'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!visiblePeople.length && (
              <TableRow>
                <TableCell colSpan={5} className={classes.empty}>
                  No people match this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Centre changes are not cosmetic — the backend's m2m_changed signal
          moves the person in and out of that centre's parent chat channels —
          so every change is spelled out before it is sent. */}
      <Dialog
        open={Boolean(pendingCentre)}
        onClose={() => setPendingCentre(null)}
        maxWidth="xs"
        fullWidth
      >
        {pendingCentre && (
          <>
            <DialogTitle>
              {pendingCentre.centre
                ? `Set ${pendingCentre.person.name}'s centre to ${pendingCentre.centre.name}?`
                : `Remove ${pendingCentre.person.name}'s centre?`}
            </DialogTitle>
            <DialogContent>
              {centresBeingRemoved.length > 0 && (
                <Typography variant="body2">
                  Removes:{' '}
                  <strong>
                    {centresBeingRemoved.map((each) => each.name).join(', ')}
                  </strong>
                </Typography>
              )}
              <Typography variant="body2" className={classes.warning}>
                {centresBeingRemoved.length > 0 &&
                  `They will also be removed from the parent chat channels of ${
                    centresBeingRemoved.length > 1
                      ? 'those centres'
                      : 'that centre'
                  }. `}
                {pendingCentre.centre &&
                  `They will be added to ${pendingCentre.centre.name}'s parent chat channels.`}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingCentre(null)}>Cancel</Button>
              <Button
                onClick={confirmCentreChange}
                color="primary"
                variant="contained"
              >
                {pendingCentre.centre ? 'Set centre' : 'Remove centre'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

People.propTypes = {
  people: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
  centres: PropTypes.array.isRequired,
  roleFilter: PropTypes.string.isRequired,
  currentUserUuid: PropTypes.string,
  savingUuid: PropTypes.string,
  onRoleFilterChange: PropTypes.func.isRequired,
  onAssignRole: PropTypes.func.isRequired,
  onAssignCentre: PropTypes.func.isRequired,
  onToggleAccess: PropTypes.func.isRequired
}

export default People
